import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercentage } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const CICalculator = () => {
    const [principalAmount, setPrincipalAmount] = useState('');
    const [monthlyContribution, setMonthlyContribution] = useState('');
    const [investmentTenure, setInvestmentTenure] = useState('');
    const [annualRate, setAnnualRate] = useState('');

    const [investedAmount, setInvestedAmount] = useState(null);
    const [interestEarned, setInterestEarned] = useState(null);
    const [maturityAmount, setMaturityAmount] = useState(null);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    }

    const calculateCI = () => {
        let P = parseFloat(principalAmount) || 0;
        const PMT = parseFloat(monthlyContribution) || 0;
        const T = parseFloat(investmentTenure);
        const R = parseFloat(annualRate) / 100;
        const N = T * 12;
        const monthlyRate = R / 12;

        if (T > 0 && R >= 0 && (P >= 0 || PMT > 0)) {
            let finalAmount = P * Math.pow(1 + monthlyRate, N);
            if (PMT > 0) {
                finalAmount += PMT * ((Math.pow(1 + monthlyRate, N) - 1) / monthlyRate);
            }

            const totalInvestedPrincipal = P + (PMT * N);
            const calculatedInterestEarned = finalAmount - totalInvestedPrincipal;

            setInvestedAmount(totalInvestedPrincipal.toFixed(2));
            setInterestEarned(calculatedInterestEarned.toFixed(2));
            setMaturityAmount(finalAmount.toFixed(2));
        } else {
            setInvestedAmount(null);
            setInterestEarned(null);
            setMaturityAmount(null);
        }
    };

    return (
        <div>
            <Helmet>
                <title>Compound Interest Calculator | Maximize Your Investment Growth</title>
                <meta name="description" content="Compute your compound interest quickly with our user-friendly CI calculator. See how your investments can grow exponentially over time." />
                <link rel="canonical" href="https://policysamjho.com/calculators/ci-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Compound Interest Calculator",
                        "description": "Compute your compound interest quickly with our user-friendly CI calculator. See how your investments can grow exponentially over time.",
                        "url": "https://policysamjho.com/calculators/ci-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className='calculator-back-button'>
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faPercentage} className="calculator-header-icon" />
                            <h1 className="calculator-title">Compound Interest Calculator</h1>
                        </div>
                        <p className="calculator-description">Compounding is the process of earning interest on past earnings. A compound interest calculator can help you figure out how much you will investments will grow if you invest your money into an account and earn interest on your investment for a specific period of time at a pre-determined rate of interest. Compounding is the basis of how your investment grows in the case of a variety of investments such as fixed deposit, recurring deposit and even Public Provident Fund (PPF). Read on to know details of how a compound interest calculator works.</p>
                    </div>
                    <div className={`calculator-main-section ${maturityAmount === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="principalAmount">Amount you can invest today (₹)</label>
                                    <input
                                        type="number"
                                        id="principalAmount"
                                        value={principalAmount}
                                        onChange={(e) => setPrincipalAmount(e.target.value)}
                                        placeholder="e.g. 10000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="monthlyContribution">Monthly Contribution (₹)</label>
                                    <input
                                        type="number"
                                        id="monthlyContribution"
                                        value={monthlyContribution}
                                        onChange={(e) => setMonthlyContribution(e.target.value)}
                                        placeholder="e.g. 500"
                                    />
                                </div>
                            </div>

                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="investmentTenure">I want to invest for (Years)</label>
                                    <input
                                        type="number"
                                        id="investmentTenure"
                                        value={investmentTenure}
                                        onChange={(e) => setInvestmentTenure(e.target.value)}
                                        placeholder="e.g. 5"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="annualRate">Expected rate of returns (p.a.) (%)</label>
                                    <input
                                        type="number"
                                        id="annualRate"
                                        value={annualRate}
                                        onChange={(e) => setAnnualRate(e.target.value)}
                                        placeholder="e.g. 12"
                                    />
                                </div>
                            </div>
                            <button onClick={calculateCI} className="calculate-button">Calculate</button>
                        </div>

                        {maturityAmount !== null && (
                            <div className="calculator-result-section">
                                <h2>You Get</h2>
                                <div className="result-item">
                                    <span>Invested amount</span>
                                    <span className="result-value">₹{investedAmount}</span>
                                </div>
                                <div className="result-item">
                                    <span>Interest earned</span>
                                    <span className="result-value">₹{interestEarned}</span>
                                </div>
                                <div className="result-item">
                                    <span>Maturity amount</span>
                                    <span className="result-value">₹{maturityAmount}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default CICalculator;